import { inject, Injectable, signal } from '@angular/core';
import { map, Observable, tap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { StateService } from '../../../services/state.service';
import { withCache } from '@ngneat/cashew';
import { Filters, FilterType } from '../../../types/filters.type';
import { Params } from '@angular/router';
import { ITaxon } from '../../../services/spree-client/storefront';

type OptionType = {
  name: string;
  presentation: string;
  option_values: {
    id: string | number;
    name: string;
    presentation: string;
  }[];
};

type ProductType = {
  name: string;
  presentation: string;
  values: {
    name: string;
    presentation: string;
  }[];
};

type FiltersJSON = {
  [key: string]: {
    option_types: OptionType[];
    product_types: ProductType[];
  };
};

@Injectable({ providedIn: 'root' })
export class FiltersService {
  private client = inject(HttpClient);
  private state = inject(StateService);
  private apiUrl = this.state.apiUrl;

  private _isAnyFilterSelected = false;
  isAnyFilterSelected = signal(false);
  isFiltersOpen = signal(false);

  getForTaxon({ data: { id } }: ITaxon, query: Params): Observable<Filters> {
    return this.client
      .get<FiltersJSON>(`${this.apiUrl}/files/${id}-filters.json`, {
        context: withCache(),
      })
      .pipe(
        map((response) => {
          this._isAnyFilterSelected = false;
          const locale = this.state.getActiveLocale();

          return {
            productTypes: response[locale].product_types.map((type) =>
              this.typesMapper<FilterType>(type, query),
            ),
            optionTypes: response[locale].option_types.map((type) =>
              this.typesMapper<FilterType>(type, query),
            ),
          };
        }),
        tap(() => this.isAnyFilterSelected.set(this._isAnyFilterSelected)),
      );
  }

  private typesMapper<ReturnType extends FilterType>(
    type: ProductType | OptionType,
    query: Params,
  ): ReturnType {
    const toMap = 'option_values' in type ? type.option_values : type.values;

    const mappedValues = toMap.map((v) => {
      const isSelected = isFilterSelected(type.presentation, v.presentation, query);

      if (isSelected) {
        this._isAnyFilterSelected = true;
      }

      return {
        ...v,
        isSelected,
      };
    });

    const selectedOption = mappedValues.find((v) => v.isSelected);

    return {
      values: mappedValues,
      name: type.name,
      presentation: type.presentation,
      selectedValue: selectedOption,
    } as ReturnType;
  }
}

const filterTypeReducer = (acc: FilterType[], curr: FilterType): FilterType[] => {
  const filterWithSelectedValues = {
    ...curr,
    values: curr.values.filter((v) => v.isSelected),
  } as FilterType;

  if (filterWithSelectedValues.values.length) {
    acc.push(filterWithSelectedValues);
  }

  return acc;
};

export function getSelectedFilters(filters: Filters): Filters {
  return {
    optionTypes: filters.optionTypes.reduce(filterTypeReducer, []),
    productTypes: filters.productTypes.reduce(filterTypeReducer, []),
  } as Filters;
}

export function getSelectedFiltersAsParams(filters?: Filters): Params {
  const allFilters = [...(filters?.productTypes ?? []), ...(filters?.optionTypes ?? [])];

  return allFilters.reduce((acc, curr) => {
    const filterWithSelectedValues = {
      ...curr,
      values: curr.selectedValue ? [curr.selectedValue] : [],
    } as FilterType;

    if (filterWithSelectedValues.values.length) {
      acc[filterWithSelectedValues.presentation] = filterWithSelectedValues.values.map(
        (value) => value.presentation,
      );
    }

    return acc;
  }, {} as Params);
}

function isFilterSelected(name: string, value: string, query: Params): boolean {
  const filter = query[name];

  if (!filter) {
    return false;
  }

  return filter instanceof Array ? filter.includes(value) : filter === value;
}
